import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import shopifyLogo from "@/assets/partners/shopify.svg";
import webflowLogo from "@/assets/partners/webflow.svg";
import googleLogo from "@/assets/partners/google-workspace.svg";
import kinstaLogo from "@/assets/partners/kinsta.svg";
import klaviyoLogo from "@/assets/partners/klaviyo.svg";
import weglotLogo from "@/assets/partners/weglot.svg";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";

const PARTNERS = [
  { name: "Shopify Partner", logo: shopifyLogo, link: "/shopify", height: "h-7 sm:h-8 lg:h-9" },
  { name: "Webflow Certified", logo: webflowLogo, link: "/webflow", height: "h-6 sm:h-7 lg:h-8" },
  { name: "Google Workspace", logo: googleLogo, link: "/solutions/google-workspace", height: "h-7 sm:h-8 lg:h-9" },
  { name: "Kinsta", logo: kinstaLogo, link: "/solutions/kinsta", height: "h-6 sm:h-7 lg:h-8" },
  { name: "Klaviyo", logo: klaviyoLogo, link: "/solutions/klaviyo", height: "h-5 sm:h-6 lg:h-7" },
  { name: "Weglot", logo: weglotLogo, link: "/solutions/weglot", height: "h-5 sm:h-6 lg:h-7" },
];

export function TechPartners() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="py-10 lg:py-24 bg-background border-t border-border">
      <div className="container px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.p
            className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("Our Partners")}
          </motion.p>
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-balance"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {t("Certified. Trusted.")} <span className="text-gradient">{t("Integrated.")}</span>
          </motion.h2>
        </div>

        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 items-center justify-items-center gap-8 sm:gap-10 lg:gap-16 max-w-5xl mx-auto mb-10"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {PARTNERS.map((p) => (
            <Link
              key={p.name}
              to={localePath(p.link)}
              className="group flex items-center justify-center"
            >
              <div className={`${p.height} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                <img
                  src={p.logo}
                  alt={p.name}
                  className="h-full w-auto object-contain grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
                />
              </div>
            </Link>
          ))}
        </motion.div>

        <motion.p
          className="text-sm text-muted-foreground text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.25 }}
        >
          {t("We work with the best tools to deliver the best results.")}
        </motion.p>
      </div>
    </section>
  );
}
